import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Download, Loader2, Pencil, Plus, Search, Upload, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StaffFormDialog } from '@/components/staffMaster/StaffFormDialog';
import { StaffImportDialog } from '@/components/staffMaster/StaffImportDialog';
import { TransferDialog } from '@/components/staffAttendance/ManpowerDialogs';
import { staffMasterApi, canEditStaff, labelOf, GENDERS, WORKER_TYPES, COLLARS, ROLL_STATUSES, PAY_TYPES } from '@/services/staffMasterApi';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { errMsg, withPool } from '@/services/staffAttendanceApi';

const PAGE = 50;
const FILTERS = { search: '', section: '', gender: '', worker_type: '', collar: '', pay_type: '', roll_status: '' };
const COLLAR_FILTER = [...COLLARS, { value: 'none', label: 'Collar not set' }];
const collarOf = (s) => s.collar || 'none';
const PAY_FILTER = [...PAY_TYPES, { value: 'none', label: 'Pay type not set' }];
const payOf = (s) => (s.worker_type === 'contract' ? 'contract' : s.pay_type || 'none');
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const PayCell = ({ s }) => {
  const p = payOf(s);
  if (p === 'contract') return <span className="text-xs text-gray-400 whitespace-nowrap">Agency</span>;
  if (p === 'none') return <span className="text-xs text-gray-400 whitespace-nowrap">Not set</span>;
  return (
    <div className="whitespace-nowrap">
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p === 'fixed' ? 'bg-amber-100 text-amber-800' : 'bg-teal-50 text-teal-700 ring-1 ring-teal-200'}`}>{labelOf(PAY_TYPES, p)}</span>
      {p === 'fixed' && s.fixed_pay != null && <div className="mt-0.5 text-xs tabular-nums text-gray-600" data-testid={`staff-fixed-pay-${s.staff_id}`}>{inr(s.fixed_pay)} / month</div>}
    </div>
  );
};

const FilterSelect = ({ value, onChange, options, all, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm">
    <option value="">{all}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const Stat = ({ label, value, testId, tone = 'text-[#1A1F2E]' }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</p>
  </CardContent></Card>
);

const matches = (s, f) => {
  const q = f.search.trim().toLowerCase();
  return (!q || [s.staff_id, s.staff_name, s.designation, s.section, s.essl_code].some((v) => String(v || '').toLowerCase().includes(q)))
    && (!f.section || s.section === f.section) && (!f.gender || s.gender === f.gender)
    && (!f.worker_type || s.worker_type === f.worker_type) && (!f.roll_status || s.roll_status === f.roll_status)
    && (!f.collar || collarOf(s) === f.collar) && (!f.pay_type || payOf(s) === f.pay_type);
};

const StaffRow = ({ s, canEdit, onEdit, onTransfer }) => (
  <TableRow data-testid={`staff-row-${s.staff_id}`} className={s.roll_status === 'left' ? 'bg-gray-50 text-gray-500' : ''}>
    <TableCell className="font-mono font-medium">{s.staff_id}{s.essl_code && <div className="text-[11px] font-normal text-gray-500" data-testid={`staff-essl-${s.staff_id}`}>eSSL {s.essl_code}</div>}</TableCell>
    <TableCell className="font-medium text-gray-900">{s.staff_name}</TableCell>
    <TableCell>{s.designation || '—'}</TableCell>
    <TableCell>{s.section || '—'}</TableCell>
    <TableCell data-testid={`staff-gender-${s.staff_id}`}>{labelOf(GENDERS, s.gender) || '—'}</TableCell>
    <TableCell data-testid={`staff-worker-type-${s.staff_id}`}>
      {labelOf(WORKER_TYPES, s.worker_type) || '—'}
      {s.worker_type === 'contract' && s.agency && <div className="text-xs text-gray-500" data-testid={`staff-agency-${s.staff_id}`}>{s.agency}</div>}
    </TableCell>
    <TableCell data-testid={`staff-pay-type-${s.staff_id}`}><PayCell s={s} /></TableCell>
    <TableCell data-testid={`staff-collar-${s.staff_id}`}>
      {s.collar ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.collar === 'white' ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' : 'bg-blue-800 text-white'}`}>{labelOf(COLLARS, s.collar)}</span>
        : <span className="text-xs text-gray-400">Not set</span>}
    </TableCell>
    <TableCell>{s.doj || '—'}</TableCell>
    <TableCell>
      <Badge className={s.roll_status === 'left' ? 'bg-rose-100 text-rose-700 hover:bg-rose-100' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'} data-testid={`staff-roll-${s.staff_id}`}>
        {s.roll_status === 'left' ? `Left${s.left_date ? ` · ${s.left_date}` : ''}` : 'In Roll'}
      </Badge>
    </TableCell>
    {canEdit && <TableCell className="text-right whitespace-nowrap">
      {s.roll_status !== 'left' && <Button size="sm" variant="ghost" title="Transfer section" onClick={() => onTransfer(s)} data-testid={`staff-transfer-${s.staff_id}`}><ArrowRightLeft className="w-4 h-4" /></Button>}
      <Button size="sm" variant="ghost" onClick={() => onEdit(s)} data-testid={`staff-edit-${s.staff_id}`}><Pencil className="w-4 h-4" /></Button>
    </TableCell>}
  </TableRow>
);

export default function StaffMaster() {
  const navigate = useNavigate();
  const canEdit = canEditStaff();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState(FILTERS);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(undefined);
  const [importing, setImporting] = useState(false);
  const [transferring, setTransferring] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => staffMasterApi.list({}).then((d) => setRows(d.staff)).catch((e) => toast.error(errMsg(e, 'Failed to load staff'))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [f]);

  const sections = useMemo(() => [...new Set((rows || []).map((s) => s.section).filter(Boolean))].sort(), [rows]);
  const agencies = useMemo(() => [...new Set((rows || []).map((s) => s.agency).filter(Boolean))].sort(), [rows]);
  const filtered = useMemo(() => (rows || []).filter((s) => matches(s, f)), [rows, f]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const count = (k, v) => filtered.filter((s) => s[k] === v).length;
  const collarSplit = useMemo(() => {
    const out = { white: { staff: 0, value: 0 }, blue: { staff: 0, value: 0 }, none: { staff: 0, value: 0 } };
    (rows || []).filter((s) => matches(s, { ...f, collar: '' })).forEach((s) => { out[collarOf(s)].staff += 1; out[collarOf(s)].value += 1; });
    return out;
  }, [rows, f]);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const doExport = async () => {
    setExporting(true);
    try { await staffMasterApi.exportCsv(f); } catch (e) { toast.error(errMsg(e, 'Export failed')); } finally { setExporting(false); }
  };
  const saved = () => { setEditing(undefined); load(); };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="staff-master-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/10" data-testid="staff-master-back"><ArrowLeft className="w-5 h-5" /></Button>
          <Users className="w-6 h-6 text-amber-400" />
          <h1 className="text-xl font-bold" data-testid="staff-master-title">Staff Master</h1>
          {canEdit && (
            <div className="ml-auto flex flex-wrap gap-2">
              <Button variant="outline" onClick={doExport} disabled={exporting || !rows} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="staff-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Export CSV
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="staff-import-btn"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>
              <Button onClick={() => setEditing(null)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="staff-add-btn"><Plus className="w-4 h-4 mr-1" />Add Staff</Button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Staff" value={filtered.length} testId="stat-total" />
          <Stat label="In Roll" value={count('roll_status', 'in_roll')} testId="stat-in-roll" tone="text-emerald-700" />
          <Stat label="Left" value={count('roll_status', 'left')} testId="stat-left" tone="text-rose-700" />
          <Stat label="Regular" value={count('worker_type', 'regular')} testId="stat-regular" />
          <Stat label="Contract" value={count('worker_type', 'contract')} testId="stat-contract" />
        </div>
        {rows && <CollarTiles split={collarSplit} value={f.collar} onPick={(v) => set({ collar: v })} metric="Staff" testId="staff-collar-tiles" />}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input value={f.search} onChange={(e) => set({ search: e.target.value })} placeholder="Search ID, name, designation, section" className="pl-9 bg-white" data-testid="staff-search-input" />
          </div>
          <FilterSelect value={f.section} onChange={(v) => set({ section: v })} options={sections.map((s) => ({ value: s, label: s }))} all="All sections" testId="staff-filter-section" />
          <FilterSelect value={f.gender} onChange={(v) => set({ gender: v })} options={GENDERS} all="All genders" testId="staff-filter-gender" />
          <FilterSelect value={f.worker_type} onChange={(v) => set({ worker_type: v })} options={WORKER_TYPES} all="Regular + Contract" testId="staff-filter-worker-type" />
          <FilterSelect value={f.collar} onChange={(v) => set({ collar: v })} options={COLLAR_FILTER} all="All collars" testId="staff-filter-collar" />
          <FilterSelect value={f.pay_type} onChange={(v) => set({ pay_type: v })} options={PAY_FILTER} all="All pay types" testId="staff-filter-pay-type" />
          <FilterSelect value={f.roll_status} onChange={(v) => set({ roll_status: v })} options={ROLL_STATUSES} all="In Roll + Left" testId="staff-filter-roll" />
          <Button variant="ghost" onClick={() => setF(FILTERS)} data-testid="staff-clear-filters">Clear</Button>
        </div>
        <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
          {!rows ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-amber-600" /> : (
            <Table data-testid="staff-table">
              <TableHeader><TableRow>
                {['Staff ID', 'Name', 'Designation', 'Section', 'Gender', 'Worker Type', 'Pay', 'Collar', 'DOJ', 'In Roll / Left'].map((h) => <TableHead key={h}>{h}</TableHead>)}
                {canEdit && <TableHead />}
              </TableRow></TableHeader>
              <TableBody>
                {filtered.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-gray-500" data-testid="staff-empty">No staff match these filters</TableCell></TableRow>}
                {filtered.slice((page - 1) * PAGE, page * PAGE).map((s) => <StaffRow key={s.staff_id} s={s} canEdit={canEdit} onEdit={setEditing} onTransfer={setTransferring} />)}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="staff-prev-page">Prev</Button>
            <span className="text-sm text-gray-600" data-testid="staff-page-info">Page {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="staff-next-page">Next</Button>
          </div>
        )}
      </main>
      <StaffFormDialog open={editing !== undefined} staff={editing} sections={sections} agencies={agencies} onClose={() => setEditing(undefined)} onSaved={saved} />
      <StaffImportDialog open={importing} onClose={() => setImporting(false)} onDone={load} />
      <TransferDialog open={!!transferring} staff={transferring} sections={withPool(sections)} onClose={() => setTransferring(null)} onDone={() => { setTransferring(null); load(); }} />
    </div>
  );
}
